'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { debug } from '@/lib/utils/debug';
import { getEnvVariable } from "@/lib/utils/env";;
import { isChunkLoadError, recoverFromChunkError } from '@/lib/utils/chunkError';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // A stale-chunk deploy is recoverable: pull a fresh build and rerender
        // instead of stranding the user on the fallback screen.
        if (isChunkLoadError(error) && recoverFromChunkError()) {
            return;
        }

        debug.error('ErrorBoundary caught an error:', error, {
            componentStack: errorInfo.componentStack,
        });

        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex flex-col items-center justify-center min-h-screen p-4" data-testid="error-boundary-fallback">
                    <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
                    {this.state.error && (
                        <div className="bg-red-50 border border-red-200 rounded p-4 max-w-2xl w-full">
                            <p className="text-red-800 font-mono text-sm">
                                {this.state.error.message}
                            </p>
                            {getEnvVariable('NODE_ENV') === 'development' && this.state.error.stack && (
                                <pre className="mt-4 text-xs text-red-600 overflow-auto">
                                    {this.state.error.stack}
                                </pre>
                            )}
                        </div>
                    )}
                    <button
                        onClick={() => {
                            this.setState({ hasError: false, error: null });
                            window.location.reload();
                        }}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Reload Page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

